import { g, layers } from '@hcie/core';
import { EventBus } from '@hcie/shared';
import { getMaskBounds } from './mask-logic';
import { getFC, setFloatingContent } from './raster-transform-action';

/**
 * @file selection-transform-ui.ts
 * @description UI Controls for selection transform operations: handles, dragging, scaling, rotating
 */

export class SelectionTransformUI {
    public active: boolean = false;
    public overlay: HTMLDivElement | null = null;
    public controls: HTMLDivElement | null = null;
    public handles: HTMLDivElement[] = [];
    public scaleFactor: { x: number, y: number } = { x: 1, y: 1 };
    
    public dragStartX: number = 0;
    public dragStartY: number = 0;
    public originalShapes: any[] | null = null;
    public originalLayerIndex: number = -1;
    public isDragging: boolean = false;
    public isRotating: boolean = false;
    public isScaling: boolean = false;
    public activeHandleIndex: number = -1;

    public angle: number = 0;
    public scaleX: number = 1;
    public scaleY: number = 1;

    public originalImageData: ImageData | null = null;
    public originalBounds: { x: number, y: number, w: number, h: number } | null = null;

    constructor() {
        this.init();
    }

    init() {
        if (typeof document === 'undefined') return;
        this.overlay = document.createElement('div');
        this.overlay.id = 'selectionTransformOverlay';
        this.overlay.style.cssText = `
            position: absolute;
            border: 1px dashed #fff;
            box-shadow: 0 0 0 1px #000;
            pointer-events: none;
            display: none;
            z-index: 10000;
            overflow: visible;
        `;

        // 8 Scale Handles + 1 Rotate Handle
        const handleCursors = ['nw-resize', 'n-resize', 'ne-resize', 'w-resize', 'e-resize', 'sw-resize', 's-resize', 'se-resize'];
        for (let i = 0; i < 8; i++) {
            const h = document.createElement('div');
            h.classList.add('transform-handle');
            h.style.cssText = `
                position: absolute;
                width: 10px;
                height: 10px;
                background: #fff;
                border: 1px solid #000;
                pointer-events: auto;
                cursor: ${handleCursors[i]};
            `;
            h.addEventListener('mousedown', (e) => this.onScaleStart(i, e));
            this.handles.push(h);
            this.overlay.appendChild(h);
        }

        // Rotate Handle (Top stem)
        const rStem = document.createElement('div');
        rStem.id = 'rotateStem';
        rStem.style.cssText = 'position: absolute; width: 1px; height: 20px; background: #fff; box-shadow: 0 0 0 1px #000; pointer-events: none; opacity: 0.8;';
        this.overlay.appendChild(rStem);

        const rHandle = document.createElement('div');
        rHandle.style.cssText = `
            position: absolute;
            width: 12px;
            height: 12px;
            background: #007bff;
            border: 2px solid #fff;
            border-radius: 50%;
            pointer-events: auto;
        `;
        const rotateCursor = `url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiBmaWxsPSJub25lIiBzdHJva2U9ImJsYWNrIiBzdHJva2Utd2lkdGg9IjIiPjxwYXRoIGQ9Ik0zIDl2Nk0zIDE1aDZNMTggNXY2TTE4IDExaC02TTEyIDIxYzQuOTcgMCA5LTQuMDMgOS05IDAtMS4yNi0uMjYtMi40NS0uNzMtMy41M00zIDEyYzAgNC45NyA0LjAzIDkgOSA5IDEuMTcgMCAyLjI4LS4yMiAzLjMtLjYyIi8+PC9zdmc+") 12 12, auto`;
        rHandle.style.cursor = rotateCursor;
        rHandle.addEventListener('mouseenter', () => { rHandle.style.cursor = rotateCursor; });
        rHandle.addEventListener('mousedown', (e) => this.onRotateStart(e));
        this.overlay.appendChild(rHandle);
        this.handles.push(rHandle); // 9th hidden handle

        this.controls = document.createElement('div');
        this.controls.id = 'transformControls';
        this.controls.style.cssText = `
            position: absolute;
            bottom: -40px;
            left: 50%;
            transform: translateX(-50%);
            display: flex;
            gap: 5px;
            pointer-events: auto;
            white-space: nowrap;
        `;

        const okBtn = document.createElement('div');
        okBtn.innerHTML = '✓ OK';
        okBtn.style.cssText = 'background: #28a745; color: #fff; padding: 4px 10px; cursor: pointer; border-radius: 3px; font-size: 11px; font-weight: bold; user-select: none;';
        okBtn.onclick = (e) => { e.stopPropagation(); this.commit(); };

        const cancelBtn = document.createElement('div');
        cancelBtn.innerHTML = '✕ Cancel';
        cancelBtn.style.cssText = 'background: #dc3545; color: #fff; padding: 4px 10px; cursor: pointer; border-radius: 3px; font-size: 11px; font-weight: bold; user-select: none;';
        cancelBtn.onclick = (e) => { e.stopPropagation(); this.cancel(); };

        this.controls.appendChild(okBtn);
        this.controls.appendChild(cancelBtn);
        this.overlay.appendChild(this.controls);

        const wrapper = document.getElementById('canvasWrapper');
        if (wrapper) wrapper.appendChild(this.overlay);

        // [CRITICAL_LOGIC_START]
        // #1350 FIX: Listen for zoom/render events to keep overlay synced with canvas
        EventBus.on('RENDER_LAYERS', () => {
            if (this.active) this.updatePosition();
        });
        
        // Also listen for window zoom events if any
        window.addEventListener('zoomChanged', () => {
            if (this.active) this.updatePosition();
        });
        // [CRITICAL_LOGIC_END]
    }

    onDragStart(e: MouseEvent) {
        if (this.isScaling || this.isRotating) return;
        this.isDragging = true;
        this.dragStartX = e.clientX;
        this.dragStartY = e.clientY;

        const onMouseMove = (me: MouseEvent) => {
            if (!this.isDragging) return;
            this.updateScaleFactor();
            const dx = (me.clientX - this.dragStartX) * this.scaleFactor.x;
            const dy = (me.clientY - this.dragStartY) * this.scaleFactor.y;
            
            // Imported from transformer
            import('./transformer').then(module => {
                module.moveSelectionContent(dx, dy);
            });
            
            this.dragStartX = me.clientX;
            this.dragStartY = me.clientY;
        };

        const onMouseUp = () => {
            this.isDragging = false;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        e.stopPropagation();
        e.preventDefault();
    }

    onRotateStart(e: MouseEvent) {
        this.isRotating = true;
        const rect = this.overlay!.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const baseAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) - this.angle;

        const onMouseMove = (me: MouseEvent) => {
            if (!this.isRotating) return;
            const newAngle = Math.atan2(me.clientY - centerY, me.clientX - centerX);
            this.angle = newAngle - baseAngle;
            const fc = getFC();
            if (fc) {
                fc.angle = this.angle;
                setFloatingContent(fc);
                this.updatePosition();
                EventBus.emit('RENDER_LAYERS');
                EventBus.emit('DRAWING_STATUS_UPDATE');
            }
        };

        const onMouseUp = () => {
            this.isRotating = false;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        e.stopPropagation();
        e.preventDefault();
    }

    onScaleStart(index: number, e: MouseEvent) {
        this.isScaling = true;
        this.activeHandleIndex = index;
        this.dragStartX = e.clientX;
        this.dragStartY = e.clientY;

        const origW = this.originalBounds!.w;
        const origH = this.originalBounds!.h;
        const startScaleX = this.scaleX;
        const startScaleY = this.scaleY;
        this.updateScaleFactor();

        const onMouseMove = (me: MouseEvent) => {
            if (!this.isScaling) return;
            const dx = (me.clientX - this.dragStartX) * this.scaleFactor.x;
            const dy = (me.clientY - this.dragStartY) * this.scaleFactor.y;

            // Simplified scaling logic (relative to center)
            // Handle indices: 0:TL, 1:TC, 2:TR, 3:ML, 4:MR, 5:BL, 6:BC, 7:BR
            let factorX = 0, factorY = 0;
            if ([0, 3, 5].includes(index)) factorX = -2; // Left side moves -X but increases scale
            if ([2, 4, 7].includes(index)) factorX = 2;  // Right side moves +X
            if ([0, 1, 2].includes(index)) factorY = -2; // Top side
            if ([5, 6, 7].includes(index)) factorY = 2;  // Bottom side

            const newScaleX = startScaleX + (dx * factorX) / origW;
            const newScaleY = startScaleY + (dy * factorY) / origH;

            if (factorX !== 0) this.scaleX = newScaleX;
            if (factorY !== 0) this.scaleY = newScaleY;

            // Aspect Ratio Lock (Shift)
            if (me.shiftKey && factorX !== 0 && factorY !== 0) {
                const ratio = origW / origH;
                if (Math.abs(this.scaleX) > Math.abs(this.scaleY)) {
                    this.scaleY = Math.abs(this.scaleX) * (this.scaleY < 0 ? -1 : 1);
                } else {
                    this.scaleX = Math.abs(this.scaleY) * (this.scaleX < 0 ? -1 : 1);
                }
            }

            const fc = getFC();
            if (fc) {
                fc.scaleX = this.scaleX;
                fc.scaleY = this.scaleY;
                setFloatingContent(fc);
                this.updatePosition();
                EventBus.emit('RENDER_LAYERS');
                EventBus.emit('DRAWING_STATUS_UPDATE');
            }
        };

        const onMouseUp = () => {
            this.isScaling = false;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        e.stopPropagation();
        e.preventDefault();
    }

    show(bounds: { x: number, y: number, w: number, h: number }) {
        this.active = true;
        this.originalBounds = { ...bounds };
        this.scaleX = 1;
        this.scaleY = 1;
        this.angle = 0;
        g.isTransforming = true;
        this.updatePosition();
        if (this.overlay) {
            this.overlay.style.display = 'block';
            this.overlay.style.zIndex = '10000'; // Ensure it's on top of other overlays
            // Allow clicking inside overlay to drag
            this.overlay.style.pointerEvents = 'auto'; 
            this.overlay.onmousedown = (e) => {
                if (e.target === this.overlay) this.onDragStart(e);
            };
        }
    }

    updatePosition() {
        if (!this.overlay || !this.originalBounds) return;

        // [CRITICAL_LOGIC_START]
        // Get the canvas and wrapper to compute accurate screen-space coordinates.
        const canvas = document.getElementById('drawingCanvas') as HTMLCanvasElement;
        const wrapper = document.getElementById('canvasWrapper') as HTMLElement;
        if (!canvas || !wrapper) return;

        const canvasRect = canvas.getBoundingClientRect();

        // Use offsetLeft/Top for local positioning within the absolute/relative wrapper.
        // This avoids drift caused by scroll/pan because the overlay is a child of the wrapper.
        const canvasOriginX = canvas.offsetLeft;
        const canvasOriginY = canvas.offsetTop;

        // Scale ratio: canvas CSS pixels per canvas logical pixel
        const scaleX = canvasRect.width  / Math.max(1, canvas.width);
        const scaleY = canvasRect.height / Math.max(1, canvas.height);

        const b = this.originalBounds;
        const curW = b.w * Math.max(0.001, Math.abs(this.scaleX));
        const curH = b.h * Math.max(0.001, Math.abs(this.scaleY));

        const fc = getFC();
        const fcXOffset = (fc && typeof fc.x === 'number') ? (fc.x - b.x) : 0;
        const fcYOffset = (fc && typeof fc.y === 'number') ? (fc.y - b.y) : 0;

        const logicalX = b.x + (b.w - curW) / 2 + fcXOffset;
        const logicalY = b.y + (b.h - curH) / 2 + fcYOffset;

        // Convert logical canvas coordinates → screen CSS coordinates relative to wrapper
        const screenX = canvasOriginX + logicalX * scaleX;
        const screenY = canvasOriginY + logicalY * scaleY;
        const screenW = curW * scaleX;
        const screenH = curH * scaleY;

        this.overlay.style.left      = screenX + 'px';
        this.overlay.style.top       = screenY + 'px';
        this.overlay.style.width     = screenW + 'px';
        this.overlay.style.height    = screenH + 'px';
        this.overlay.style.transform = `rotate(${this.angle}rad)`;
        // [CRITICAL_LOGIC_END]

        // Scale handle positions (relative to overlay)
        const hw = 10;
        const w = screenW;
        const h = screenH;
        const positions = [
            { l: -hw/2,   t: -hw/2   }, { l: w/2-hw/2, t: -hw/2   }, { l: w-hw/2,   t: -hw/2   },
            { l: -hw/2,   t: h/2-hw/2 },                               { l: w-hw/2,   t: h/2-hw/2 },
            { l: -hw/2,   t: h-hw/2   }, { l: w/2-hw/2, t: h-hw/2   }, { l: w-hw/2,   t: h-hw/2   }
        ];
        for (let i = 0; i < 8; i++) {
            if (this.handles[i]) {
                this.handles[i].style.left = positions[i].l + 'px';
                this.handles[i].style.top  = positions[i].t + 'px';
            }
        }

        // Rotate stem positioning (attached above overlay center)
        const stem = this.overlay.querySelector('div:nth-child(10)') as HTMLElement;
        if (stem) {
            stem.style.left = (w / 2 - 1) + 'px';
            stem.style.top  = '-20px';
        }
        if (this.handles[8]) {
            this.handles[8].style.left = (w / 2 - 6) + 'px';
            this.handles[8].style.top  = '-32px';
        }
    }

    commit() {
        import('./transformer').then(module => {
            module.finalizeMoveContent();
        });
        this.hide();
    }

    cancel() {
        const fc = getFC();
        if (fc) {
            const layer = layers[this.originalLayerIndex];
            if (layer && this.originalImageData) {
                (layer.ctx as CanvasRenderingContext2D).putImageData(this.originalImageData, 0, 0);
                if (this.originalShapes) layer.shapes = this.originalShapes;
            }
        }
        setFloatingContent(null);
        this.hide();
        EventBus.emit('RENDER_LAYERS');
    }

    hide() {
        this.active = false;
        this.angle = 0;
        this.scaleX = 1;
        this.scaleY = 1;
        g.isTransforming = false;
        if (this.overlay) {
            this.overlay.style.display = 'none';
            this.overlay.style.transform = 'none';
        }
        this.originalImageData = null;
        this.originalShapes = null;
        this.originalBounds = null;
    }

    private updateScaleFactor() {
        const canvas = document.getElementById('drawingCanvas') as HTMLCanvasElement;
        if (canvas) {
            const rect = canvas.getBoundingClientRect();
            this.scaleFactor = {
                x: canvas.width / Math.max(1, rect.width),
                y: canvas.height / Math.max(1, rect.height)
            };
        }
    }
}
